import {
  Decimal,
  DISPUTE_REASONS,
  type Dispute,
  type DisputeOutcome,
  type IncomingRefund,
  type MoneyJson,
  type ReconciliationException,
} from '@paguz/contracts';
import {
  Badge,
  Button,
  EmptyState,
  formatCop,
  MoneyText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { CheckCircle2Icon, InboxIcon, PlayIcon } from 'lucide-react';
import { useState } from 'react';
import type { AftersalesTab } from '../../app/search.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ConfirmActionDialog, SummaryRows } from '../../components/confirm-dialog.tsx';
import { Field, Select } from '../../components/form-controls.tsx';
import { ErrorState, LoadingRows, PageHeader, Panel } from '../../components/page.tsx';
import { formatBusinessDate, formatDateTime, shortId } from '../../lib/format.ts';
import { useCan } from '../../lib/session.ts';
import { slaFor, useConsoleNow } from '../../lib/sla.ts';
import { MoneyField } from '../config/setting-editor.tsx';

const route = getRouteApi('/app/postventa');

const DISPUTE_STATUS: Readonly<
  Record<
    string,
    { label: string; tone: 'highlight' | 'warning' | 'success' | 'danger' | 'outline' }
  >
> = {
  OPEN: { label: 'Abierta', tone: 'highlight' },
  UNDER_REVIEW: { label: 'En revisión', tone: 'warning' },
  ACCEPTED_FULL: { label: 'Aceptada total', tone: 'success' },
  ACCEPTED_PARTIAL: { label: 'Aceptada parcial', tone: 'success' },
  REJECTED: { label: 'Rechazada', tone: 'danger' },
  CLOSED: { label: 'Cerrada', tone: 'outline' },
};
const OUTCOME_LABEL: Readonly<Record<DisputeOutcome, string>> = {
  ACCEPTED_FULL: 'Aceptar total',
  ACCEPTED_PARTIAL: 'Aceptar parcial',
  REJECTED: 'Rechazar',
};
const DISPUTE_REASON_CODES = [
  ['MERCHANT_CONFIRMED', 'El comercio confirmó el error'],
  ['DUPLICATE_CONFIRMED', 'Cobro duplicado comprobado'],
  ['NO_EVIDENCE', 'Sin soporte suficiente'],
  ['SERVICE_DELIVERED', 'El comercio demostró la entrega'],
  ['PARTIAL_DELIVERY', 'Entrega parcial'],
] as const;
const EXCEPTION_LABEL: Readonly<Record<string, string>> = {
  MISSING_IN_BREB: 'No está en Bre-B',
  MISSING_IN_CREDIT_CORE: 'No está en el core de crédito',
  MISSING_IN_DEPOSIT_CORE: 'No está en el core de depósitos',
  AMOUNT_MISMATCH: 'Montos distintos',
  ORPHAN_INCOMING_REFUND: 'Devolución sin compra',
  STUCK_PENDING: 'Compra atascada en verificación',
  POST_CONFIRMATION_POSTING_FAILED: 'Falló el posteo tras confirmar Bre-B',
  STATUS_CONFLICT: 'Estados en conflicto',
  COMPENSATION_FAILED: 'Falló la compensación',
};

/** Disputas, devoluciones sin match y conciliación (`06-backoffice.md` §4.5–4.6). */
export function AftersalesPage() {
  const { tab = 'disputas' } = route.useSearch();
  const navigate = route.useNavigate();
  return (
    <>
      <PageHeader
        title="Disputas y conciliación"
        description="Reclamos de clientes, devoluciones de comercios por Bre-B que no encontraron su compra y cuadre diario entre Paguz, los cores y la liquidación Bre-B."
      />
      <Tabs
        value={tab}
        onValueChange={(v) => {
          void navigate({ search: { tab: v as AftersalesTab } });
        }}
      >
        <TabsList variant="line">
          <TabsTrigger value="disputas">Disputas</TabsTrigger>
          <TabsTrigger value="devoluciones">Devoluciones sin match</TabsTrigger>
          <TabsTrigger value="conciliacion">Conciliación</TabsTrigger>
        </TabsList>
        <TabsContent value="disputas">
          <Disputes />
        </TabsContent>
        <TabsContent value="devoluciones">
          <Refunds />
        </TabsContent>
        <TabsContent value="conciliacion">
          <Reconciliation />
        </TabsContent>
      </Tabs>
    </>
  );
}

function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: (readonly string[])[]) => {
    for (const k of keys) void qc.invalidateQueries({ queryKey: k });
    void qc.invalidateQueries({ queryKey: ['audit'] });
  };
}

function Disputes() {
  const canDecide = useCan('disputes:decide');
  const now = useConsoleNow();
  const invalidate = useInvalidate();
  const query = useQuery({ queryKey: queryKeys.disputes, queryFn: api.listDisputes });
  const [target, setTarget] = useState<Dispute | null>(null);
  const [outcome, setOutcome] = useState<DisputeOutcome>('ACCEPTED_FULL');
  const [amount, setAmount] = useState<MoneyJson>({ amount: '0.00', currency: 'COP' });
  const [code, setCode] = useState<string>(DISPUTE_REASON_CODES[0][0]);
  const decide = useMutation({
    mutationFn: (comment: string) =>
      api.decideDispute(target?.id ?? '', {
        outcome,
        reasonCode: code,
        comment,
        ...(outcome === 'ACCEPTED_PARTIAL' && { amount }),
      }),
    onSuccess: () => {
      invalidate(queryKeys.disputes);
      toast.success('Disputa decidida; la reversión se aplica al crédito');
    },
  });
  if (query.isPending) return <LoadingRows />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const rows = query.data.data;
  return (
    <Panel bodyClassName="p-0">
      {rows.length === 0 ? (
        <div className="p-5">
          <EmptyState icon={<InboxIcon />} title="Sin disputas" />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Disputa</TableHead>
              <TableHead>Compra</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>Abierta</TableHead>
              <TableHead>Vence</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((d) => {
              const open = d.status === 'OPEN' || d.status === 'UNDER_REVIEW';
              return (
                <TableRow key={d.id}>
                  <TableCell>
                    <code className="font-mono text-xs">{shortId(d.id)}</code>
                    <p className="max-w-72 text-xs text-muted-foreground">{d.description}</p>
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/transacciones/$paymentIntentId"
                      params={{ paymentIntentId: d.paymentIntentId }}
                      className="font-mono text-xs text-primary hover:underline"
                    >
                      {shortId(d.paymentIntentId)}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{DISPUTE_REASONS.labels[d.reason]}</TableCell>
                  <TableCell className="text-right">
                    <MoneyText value={d.amount.amount} size="sm" />
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {slaFor(d.createdAt, now).label}
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap">
                    {formatDateTime(d.dueAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={DISPUTE_STATUS[d.status]?.tone ?? 'outline'}>
                      {DISPUTE_STATUS[d.status]?.label ?? d.status}
                    </Badge>
                    {d.decidedAmount && (
                      <p className="text-xs text-muted-foreground">
                        Reversado {formatCop(d.decidedAmount.amount)}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    {open && canDecide && (
                      <Button
                        size="sm"
                        variant="highlight"
                        onClick={() => {
                          setTarget(d);
                          setOutcome('ACCEPTED_FULL');
                          setAmount({ amount: '0.00', currency: 'COP' });
                        }}
                      >
                        Decidir
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <ConfirmActionDialog
        open={target !== null}
        onOpenChange={(o) => {
          if (!o) setTarget(null);
        }}
        title="Decidir la disputa"
        description="Usa la misma decisión que POST /v1/disputes/{id}/decision. Si se acepta, la utilización se reversa y el cupo vuelve al cliente."
        confirmWord={target?.id ?? 'CONFIRMAR'}
        confirmLabel="Registrar decisión"
        destructive={outcome === 'REJECTED'}
        reasonLabel="Comentario (queda en auditoría)"
        summary={
          target && (
            <SummaryRows
              rows={[
                ['Compra', target.paymentIntentId],
                ['Monto reclamado', formatCop(target.amount.amount)],
              ]}
            />
          )
        }
        extra={
          <div className="flex flex-col gap-3">
            <Field label="Resultado" htmlFor="dsp-outcome">
              <Select
                id="dsp-outcome"
                value={outcome}
                onChange={(e) => {
                  setOutcome(e.target.value as DisputeOutcome);
                }}
              >
                {(Object.keys(OUTCOME_LABEL) as DisputeOutcome[]).map((o) => (
                  <option key={o} value={o}>
                    {OUTCOME_LABEL[o]}
                  </option>
                ))}
              </Select>
            </Field>
            {outcome === 'ACCEPTED_PARTIAL' && (
              <MoneyField label="Monto a reversar" value={amount} onChange={setAmount} />
            )}
            <Field label="Motivo codificado" htmlFor="dsp-code">
              <Select
                id="dsp-code"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
              >
                {DISPUTE_REASON_CODES.map(([c, l]) => (
                  <option key={c} value={c}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        }
        extraValid={outcome !== 'ACCEPTED_PARTIAL' || new Decimal(amount.amount).gt(0)}
        onConfirm={async (comment) => {
          await decide.mutateAsync(comment);
        }}
      />
    </Panel>
  );
}

function Refunds() {
  const canMatch = useCan('refunds:match');
  const invalidate = useInvalidate();
  const refunds = useQuery({
    queryKey: queryKeys.refunds,
    queryFn: () => api.listIncomingRefunds(false),
  });
  const purchases = useQuery({
    queryKey: queryKeys.paymentIntents({ status: 'COMPLETED' }),
    queryFn: () => api.listPaymentIntents({ status: 'COMPLETED' }),
  });
  const [target, setTarget] = useState<{ refund: IncomingRefund; pi: string } | null>(null);
  const match = useMutation({
    mutationFn: (reason: string) =>
      api.matchRefund(target?.refund.id ?? '', target?.pi ?? '', reason),
    onSuccess: () => {
      invalidate(queryKeys.refunds, queryKeys.reconExceptions);
      toast.success('Devolución asociada a la compra y aplicada al crédito');
    },
  });
  if (refunds.isPending) return <LoadingRows />;
  if (refunds.isError)
    return <ErrorState error={refunds.error} onRetry={() => void refunds.refetch()} />;
  const rows = refunds.data.data.filter((r) => r.paymentIntentId === null);
  /** Sugerencias: compras completadas con monto ≥ devolución y fecha anterior, la más cercana primero. */
  const suggest = (r: IncomingRefund) =>
    (purchases.data?.data ?? [])
      .filter(
        (p) => new Decimal(p.amount.amount).gte(r.amount.amount) && p.createdAt <= r.receivedAt,
      )
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .slice(0, 3);
  return (
    <div className="flex flex-col gap-4">
      {rows.length === 0 && (
        <Panel>
          <EmptyState
            icon={<CheckCircle2Icon />}
            title="Todas las devoluciones encontraron su compra"
          />
        </Panel>
      )}
      {rows.map((r) => (
        <Panel
          key={r.id}
          title={
            <span className="inline-flex items-center gap-2">
              <MoneyText value={r.amount.amount} size="md" /> recibido{' '}
              {formatDateTime(r.receivedAt)}
            </span>
          }
          description={`Bre-B ${r.endToEndId} · ${r.reference ? `referencia ${r.reference}` : 'sin referencia válida'}`}
          actions={<Badge variant="warning">Sin match</Badge>}
        >
          <p className="mb-2 text-xs font-semibold text-muted-foreground">
            Compras sugeridas (monto ≥ devolución, fecha cercana)
          </p>
          <ul className="flex flex-col gap-2">
            {suggest(r).map((p) => (
              <li
                key={p.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border px-3 py-2 text-sm"
              >
                <span>
                  <span className="font-semibold">{p.payeeName}</span> · {p.customer.fullName} ·{' '}
                  {formatDateTime(p.createdAt)}
                  <span className="block font-mono text-xs text-muted-foreground">{p.id}</span>
                </span>
                <span className="flex items-center gap-3">
                  <MoneyText value={p.amount.amount} size="sm" />
                  {canMatch && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setTarget({ refund: r, pi: p.id });
                      }}
                    >
                      Asociar a esta compra
                    </Button>
                  )}
                </span>
              </li>
            ))}
            {suggest(r).length === 0 && (
              <li className="text-sm text-muted-foreground">Sin compras candidatas.</li>
            )}
          </ul>
        </Panel>
      ))}
      <p className="text-xs text-muted-foreground">
        ⚠️ "Tratar como abono libre" queda pendiente: la API aún no expone esa acción.
      </p>
      <ConfirmActionDialog
        open={target !== null}
        onOpenChange={(o) => {
          if (!o) setTarget(null);
        }}
        title="Asociar la devolución a la compra"
        description="La devolución se aplica a la utilización (recalcula cuotas y restituye cupo), no como saldo suelto."
        confirmLabel="Asociar"
        summary={
          target && (
            <SummaryRows
              rows={[
                ['Devolución', formatCop(target.refund.amount.amount)],
                ['Compra', target.pi],
              ]}
            />
          )
        }
        onConfirm={async (reason) => {
          await match.mutateAsync(reason);
        }}
      />
    </div>
  );
}

function Reconciliation() {
  const canOperate = useCan('reconciliation:operate');
  const invalidate = useInvalidate();
  const now = useConsoleNow();
  const runs = useQuery({ queryKey: queryKeys.reconRuns, queryFn: api.listReconRuns });
  const exceptions = useQuery({
    queryKey: queryKeys.reconExceptions,
    queryFn: () => api.listReconExceptions('OPEN'),
  });
  const [running, setRunning] = useState(false);
  const [target, setTarget] = useState<{
    exception: ReconciliationException;
    action: 'RETRY_STEP' | 'FORCE_COMPENSATION' | 'RESOLVE';
  } | null>(null);
  const yesterday = new Date(now.getTime() - 86_400_000).toISOString().slice(0, 10);
  const run = useMutation({
    mutationFn: () => api.runReconciliation(yesterday),
    onSuccess: (r) => {
      invalidate(queryKeys.reconRuns, queryKeys.reconExceptions);
      toast.success(
        `Conciliación del ${formatBusinessDate(r.businessDate)}: ${r.status === 'COMPLETED' ? 'completada' : r.status}`,
      );
    },
  });
  const act = useMutation({
    mutationFn: (reason: string) =>
      api.reconExceptionAction(target?.exception.id ?? '', target?.action ?? 'RESOLVE', reason),
    onSuccess: () => {
      invalidate(queryKeys.reconExceptions);
      toast.success('Acción registrada');
    },
  });
  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Corridas"
        description="Paguz vs. core de crédito, core de depósitos y liquidación Bre-B por fecha de negocio."
        actions={
          canOperate && (
            <Button
              variant="highlight"
              disabled={running}
              onClick={() => {
                setRunning(true);
                run.mutate(undefined, {
                  onSettled: () => {
                    setRunning(false);
                  },
                });
              }}
            >
              <PlayIcon /> Ejecutar conciliación ({formatBusinessDate(yesterday)})
            </Button>
          )
        }
        bodyClassName="p-0"
      >
        {runs.isPending ? (
          <div className="p-5">
            <LoadingRows rows={2} />
          </div>
        ) : runs.isError ? (
          <div className="p-5">
            <ErrorState error={runs.error} />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha de negocio</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Registros por fuente</TableHead>
                <TableHead>Terminó</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.data.data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-semibold">
                    {formatBusinessDate(r.businessDate)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        r.status === 'COMPLETED'
                          ? 'success'
                          : r.status === 'FAILED'
                            ? 'danger'
                            : 'warning'
                      }
                    >
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {Object.entries(r.stats)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join(' · ')}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(r.finishedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <Panel title="Excepciones abiertas" bodyClassName="p-0">
        {exceptions.isPending ? (
          <div className="p-5">
            <LoadingRows rows={3} />
          </div>
        ) : exceptions.isError ? (
          <div className="p-5">
            <ErrorState error={exceptions.error} />
          </div>
        ) : exceptions.data.data.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={<CheckCircle2Icon />} title="Sin excepciones abiertas" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tipo</TableHead>
                <TableHead>Severidad</TableHead>
                <TableHead>Compra</TableHead>
                <TableHead>Desde</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {exceptions.data.data.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>
                    <p className="font-semibold">{EXCEPTION_LABEL[e.type] ?? e.type}</p>
                    <code className="font-mono text-xs text-muted-foreground">{e.type}</code>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        e.severity === 'CRITICAL'
                          ? 'danger'
                          : e.severity === 'HIGH'
                            ? 'warning'
                            : 'secondary'
                      }
                    >
                      {e.severity}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {e.paymentIntentId ? (
                      <Link
                        to="/transacciones/$paymentIntentId"
                        params={{ paymentIntentId: e.paymentIntentId }}
                        className="font-mono text-xs text-primary hover:underline"
                      >
                        {shortId(e.paymentIntentId)}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(e.createdAt)}</TableCell>
                  <TableCell>
                    {canOperate && (
                      <div className="flex justify-end gap-1.5">
                        {e.paymentIntentId && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setTarget({ exception: e, action: 'RETRY_STEP' });
                            }}
                          >
                            Reintentar paso
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="highlight"
                          onClick={() => {
                            setTarget({ exception: e, action: 'RESOLVE' });
                          }}
                        >
                          Marcar resuelta
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <ConfirmActionDialog
        open={target !== null}
        onOpenChange={(o) => {
          if (!o) setTarget(null);
        }}
        title={
          target?.action === 'RESOLVE'
            ? 'Marcar la excepción como resuelta'
            : 'Reintentar el paso de la saga'
        }
        description="Acción de operador con doble confirmación; queda en auditoría."
        confirmLabel={target?.action === 'RESOLVE' ? 'Marcar resuelta' : 'Reintentar'}
        onConfirm={async (reason) => {
          await act.mutateAsync(reason);
        }}
      />
    </div>
  );
}
